/**
 * MeetingSummaryCard — Compact card for the meeting summaries grid.
 *
 * Displays meeting title, date/time range with duration, source provider
 * icon + label, and attendee avatars. Styled similarly to pipeline deal cards.
 */

import { memo, useCallback, useMemo } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { getAvatarInitials } from '@/lib/crm/utils/avatar';
import {
  MEETING_NOTES_SOURCE_LABELS,
  MeetingNoteAttribution,
} from '@/lib/crm/utils/meetingNotes';
import { useOrgTimeFormat } from '@/lib/time';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { formatMeetingDateRange } from '../../lib/crm/record/utils/meetingHelpers';

// ============================================================================
// Source icons
// ============================================================================

const SOURCE_ICONS: Record<string, string> = {
  google_meet: '/assets/figma/google-meet-icon.svg',
  granola: '/assets/figma/granola-icon.svg',
  zoom: '/assets/figma/zoom-icon.svg',
  fathom: '/assets/figma/fathom-icon.svg',
};

// ============================================================================
// Attendee helpers
// ============================================================================

interface Invitation {
  email?: string;
  display_name?: string;
}

// ============================================================================
// Component
// ============================================================================

interface MeetingSummaryCardProps {
  note: CrmObject;
  meeting: CrmObject | null;
  onClick: (note: CrmObject) => void;
  ownerEntityId?: string;
}

export const MeetingSummaryCard = memo(function MeetingSummaryCard({
  note,
  meeting,
  onClick,
  ownerEntityId,
}: MeetingSummaryCardProps) {
  const { timeFormat } = useOrgTimeFormat();
  const title =
    getPropertyValue<string>(meeting?.properties?.ahoy_title) ||
    getPropertyValue<string>(note.properties?.ahoy_title) ||
    'Meeting Summary';

  const source = getPropertyValue<string>(note.properties?.ahoy_notes_source);
  const sourceLabel = source
    ? (MEETING_NOTES_SOURCE_LABELS[source] ?? source)
    : null;
  const sourceIcon = source ? SOURCE_ICONS[source] : null;

  const startTime =
    getPropertyValue<string>(meeting?.properties?.ahoy_start_time) ?? null;
  const endTime =
    getPropertyValue<string>(meeting?.properties?.ahoy_end_time) ?? null;
  const dateTimeStr = useMemo(
    () =>
      formatMeetingDateRange(startTime, endTime, undefined, timeFormat) ??
      'Unknown date',
    [startTime, endTime, timeFormat]
  );

  // Parse invitations for attendee avatars
  const invitations = useMemo(() => {
    const raw = getPropertyValue<Invitation[]>(
      meeting?.properties?.ahoy_invitations
    );
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.slice(0, 4); // Show max 4 avatars
  }, [meeting?.properties?.ahoy_invitations]);

  const handleClick = useCallback(() => {
    onClick(note);
  }, [onClick, note]);

  return (
    <button
      type='button'
      onClick={handleClick}
      className='text-left rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] p-4 hover:shadow-md transition-shadow cursor-pointer w-full'
    >
      {/* Row 1: Title */}
      <div className='mb-1'>
        <h3 className='text-sm font-semibold text-fg-primary line-clamp-1 min-w-0'>
          {title}
        </h3>
      </div>

      {/* Row 2: Date/time */}
      <p className='text-xs text-fg-tertiary mb-3 line-clamp-1'>
        {dateTimeStr}
      </p>

      {/* Row 3: Source + owner + attendees */}
      <div className='flex items-center justify-between'>
        {(sourceLabel || ownerEntityId) && (
          <div className='flex items-center gap-1.5 min-w-0'>
            <MeetingNoteAttribution
              sourceLabel={sourceLabel ?? undefined}
              sourceIcon={sourceIcon ?? undefined}
              ownerEntityId={ownerEntityId}
              orgId={note.org_id}
              interactive={false}
              variant='card'
              alwaysShowSeparator={false}
            />
          </div>
        )}

        {invitations.length > 0 && (
          <div className='flex items-center -space-x-1.5'>
            {invitations.map((inv, idx) => (
              <Avatar
                key={inv.email ?? inv.display_name ?? `attendee-${idx}`}
                size='xs'
                initials={getAvatarInitials(
                  inv.display_name || inv.email?.split('@')[0] || ''
                )}
                className='ring-2 ring-[var(--color-bg-primary)]'
              />
            ))}
          </div>
        )}
      </div>
    </button>
  );
});
